import { Control, useWatch } from 'react-hook-form';
import { DatePickerField, TextInputField } from '@brighte/form-components';
import { css } from '@emotion/css';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Columns } from '@spark-web/columns';
import { Heading } from '@spark-web/heading';
import { CheckCircleIcon } from '@spark-web/icon';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import pccBackSampleImage from '../../../assets/pcc_back_sample.svg';
import pccFrontSampleImage from '../../../assets/pcc_front_sample.svg';
import type { VerifyIncomeFormValues } from './verifyIncomeStatus';

// A Centrelink CRN is exactly 10 characters: 9 digits followed by 1 letter (e.g. 203456789A).
export const CONCESSION_CARD_CRN_PATTERN = /^\d{9}[A-Za-z]$/;
export const isValidConcessionCardCrn = (crn?: string | null): boolean =>
  !!crn && CONCESSION_CARD_CRN_PATTERN.test(crn.trim());
const CRN_ERROR_MESSAGE = 'Enter a valid CRN — 9 numbers followed by 1 letter.';

// A valid card expiry is today or a future date — an already-expired card can't be verified.
export const isCardExpiryTodayOrFuture = (expiry?: Date | string | null): boolean => {
  if (!expiry) return false;
  const expiryDate = expiry instanceof Date ? expiry : new Date(expiry);
  if (Number.isNaN(expiryDate.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const normalisedExpiry = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());
  return normalisedExpiry.getTime() >= today.getTime();
};
const EXPIRY_ERROR_MESSAGE = 'Card is already expired.';

type PensionerConcessionCardFormProps = {
  control: Control<VerifyIncomeFormValues>;
  ownerId: string;
  onVerify: () => void;
  // Enabled only once every field holds valid data.
  canVerify: boolean;
  // Loading state of the verify mutation.
  isVerifying: boolean;
  // A verify attempt failed but retries remain — prompt the owner to try again.
  verifyError: boolean;
  // Retry cap hit — hide the form + example and show the continue-for-manual-review alert.
  capReached: boolean;
  // Card verify returned ACCEPT — hide the form + example and show the success message.
  verified: boolean;
};

const cardImageClassName = css({
  width: '100%',
  maxWidth: 400,
  height: 'auto',
  display: 'block',
  marginInline: 'auto',
});

// Pensioner document path: enter the concession-card details (frontend-only for now).
function PensionerConcessionCardForm({
  control,
  ownerId,
  onVerify,
  canVerify,
  isVerifying,
  verifyError,
  capReached,
  verified,
}: PensionerConcessionCardFormProps) {
  const crn = useWatch({ control, name: `concessionCard.${ownerId}.crn` });
  // Surface the format error only once something has been typed (not on an empty field).
  const crnError = crn && !isValidConcessionCardCrn(crn) ? CRN_ERROR_MESSAGE : undefined;

  const expiry = useWatch({ control, name: `concessionCard.${ownerId}.expiry` });
  // Only flag an expired card once a date has been picked (not on an empty field).
  const expiryError = expiry && !isCardExpiryTodayOrFuture(expiry) ? EXPIRY_ERROR_MESSAGE : undefined;

  // A verified ACCEPT or a hit retry cap ends the card flow: the input fields stay visible
  // (read-only reference of what was entered) but the Verify Card button and the example
  // card are hidden, leaving the relevant status message in their place.
  const flowEnded = verified || capReached;

  return (
    <Stack gap="large" marginTop="large">
      <TextInputField
        name={`concessionCard.${ownerId}.name`}
        control={control}
        label="Name as shown on Pensioner Concession Card"
      />
      {/* CRN sits full-width above the expiry/DOB row: its label is too long to fit alongside
          the two date fields on desktop. */}
      <TextInputField
        name={`concessionCard.${ownerId}.crn`}
        control={control}
        label="Customer Reference Number (CRN)"
        maxLength={10}
        FieldProps={crnError ? { tone: 'critical', message: crnError } : undefined}
      />
      <Columns gap="xxlarge" collapseBelow="tablet">
        <DatePickerField
          name={`concessionCard.${ownerId}.expiry`}
          control={control}
          label="Card expiry date"
          FieldProps={expiryError ? { tone: 'critical', message: expiryError } : undefined}
        />
        <DatePickerField name={`concessionCard.${ownerId}.birthdate`} control={control} label="Date of birth" />
      </Columns>
      {verified ? (
        <Box
          display="flex"
          alignItems="center"
          gap="small"
          background="positiveLow"
          padding="medium"
          borderRadius="medium"
        >
          <CheckCircleIcon tone="positive" size="small" />
          <Text size="small">Pensioner Concession Card is verified.</Text>
        </Box>
      ) : capReached ? (
        <Alert tone="caution">
          <Text size="small">
            We were unable to verify your ID. You can continue on to the application and our credit team will contact
            you to verify your pension card.
          </Text>
        </Alert>
      ) : (
        verifyError && (
          <Alert tone="critical">
            <Text size="small">
              We weren&apos;t able to verify your details. Please check you&apos;ve entered them correctly
            </Text>
          </Alert>
        )
      )}
      {!flowEnded && (
        <>
          <Stack width="full">
            <Button onClick={onVerify} disabled={!canVerify || isVerifying} loading={isVerifying}>
              Verify Card
            </Button>
          </Stack>
          <Box background="infoLow" borderRadius="medium" padding="large">
            <Stack gap="medium">
              <Heading level="4">Example of Pensioner Concession Card</Heading>
              <Box
                display="flex"
                flexDirection={{ mobile: 'column', tablet: 'row' }}
                gap="medium"
                alignItems={{ mobile: 'stretch', tablet: 'center' }}
              >
                <Box flex={1} minWidth={0}>
                  <img
                    src={pccFrontSampleImage}
                    alt="Front of an example Pensioner Concession Card"
                    className={cardImageClassName}
                  />
                </Box>
                <Box flex={1} minWidth={0}>
                  <img
                    src={pccBackSampleImage}
                    alt="Back of an example Pensioner Concession Card"
                    className={cardImageClassName}
                  />
                </Box>
              </Box>
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  );
}

export default PensionerConcessionCardForm;
